import type { TestId } from '@repo/test-id';
import type { ReactNode } from 'react';
import { CheckIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import { Badge, CarouselSlide } from '@repo/ui';

type SlideColorTheme = 'blue' | 'gold' | 'green' | 'orange';

type Point = {
  label: string;
  isSoon?: boolean;
};

interface SlideTheme {
  backgroundColor: string;
  accentColor: string;
  headerColor: string;
  pointsColor: string;
  iconColor: string;
}

const colorThemes: Record<SlideColorTheme, SlideTheme> = {
  blue: {
    backgroundColor: 'bg-[#101E45]',
    accentColor: 'bg-[#5492F5]',
    headerColor: 'text-white',
    pointsColor: 'text-white',
    iconColor: 'text-[#5492F5]',
  },
  gold: {
    backgroundColor: 'bg-[#3D2600]',
    accentColor: 'bg-[#7c500a]',
    headerColor: 'text-white',
    pointsColor: 'text-white',
    iconColor: 'text-brand-2',
  },
  green: {
    backgroundColor: 'bg-[#0C3A29]',
    accentColor: 'bg-[#0A9260]',
    headerColor: 'text-white',
    pointsColor: 'text-white',
    iconColor: 'text-[#0A9260]',
  },
  orange: {
    backgroundColor: 'bg-[#501E00]',
    accentColor: 'bg-[#F57160]',
    headerColor: 'text-white',
    pointsColor: 'text-white',
    iconColor: 'text-[#F57160]',
  },
};

interface Props {
  testId: TestId;
  title: string;
  points: Point[];
  children: ReactNode;
  theme?: SlideColorTheme;
}

export default function PromoCarouselSlide({ testId, title, points, children, theme = 'blue' }: Props) {
  const { backgroundColor, accentColor, headerColor, pointsColor, iconColor } = colorThemes[theme];

  return (
    <CarouselSlide testId={testId} className="relative">
      <div className={`h-full rounded-2xl pt-2 ${accentColor}`}>
        <div className={`flex h-full flex-col rounded-2xl ${backgroundColor}`}>
          <div className="relative -mt-3">
            <svg width="100%" viewBox="0 0 766 473" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle opacity="0.45" cx="383.5" cy="52.5" r="420.5" fill="white" fillOpacity="0.09" />
            </svg>
            <div className="absolute inset-0 flex flex-col">
              <div className="flex justify-center">{children}</div>
            </div>
          </div>
          <div className="mb-18 flex flex-1 flex-col justify-center gap-4 px-10">
            <h3 className={`text-2xl font-semibold text-balance ${headerColor}`}>{title}</h3>
            <ul className="flex flex-col gap-2">
              {points.map((point, pointIndex) => (
                <li key={pointIndex} className="flex items-start gap-2">
                  <CheckIcon color={iconColor} size={20} className="mt-1.25 shrink-0" />
                  {point.isSoon && (
                    <Badge
                      label={tr('promo:common.soon')}
                      color="success"
                      className="mt-0.75"
                      testId={testId.append(pointIndex.toString()).append('soon')}
                    />
                  )}
                  <span className={`text-base font-normal ${pointsColor}`}>{point.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </CarouselSlide>
  );
}
